import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { frozenRateFields, rateOverrideFields, rateOverrideInputFields, rateOverridePatchFields, documentCurrencyInputFields, documentCurrencyPatchFields } from './fx.js';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { InvoiceStatsSchema } from './document-stats.js';
import { DocumentDateRangeQuery, pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
const c = initContract();
/**
 * INV-L1 — a line's free quantity: zero or more, three decimals like a qty.
 * `QtyStringSchema` refuses zero (BR-SAL-00-18 is about what is SOLD), and
 * "nothing given free" is the ordinary line, so this is its own shape.
 */
export const BonusQtyStringSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,3})?$/, 'A bonus quantity is a number with up to three decimals.');
/**
 * Sales Invoices (PART 6, SAL-04 on the SAL-00 shared layout).
 *
 * Permission area: `sales` (PART 16.2.2 row 1), per action:
 *   list/get = sales.view · create = sales.create · edit draft = sales.edit ·
 *   delete draft = sales.delete · post = sales.approve (16.3: "sales.approve
 *   for confirming an invoice"). The AP Clerk holds none of these — "cannot
 *   see invoices at all".
 *
 * Lifecycle per ruling R8 / SAL-00 §7: a DRAFT is freely editable (lines
 * replaced wholesale — the grid saves as a set) and deletable; POST is the
 * one-way door that writes the SAL-00 §12 journal entry through
 * @palaccount/ledger in the same transaction; a POSTED invoice answers 409
 * with the spec's fixed sentence to any edit but the notes. CANCEL (ruling
 * A6) is the Posted → Cancelled door: it writes the reversal rather than
 * undoing the original, and both records stay on file (GEN-213). The credit
 * note is the separate SAL-05 correction and always was.
 *
 * Money never travels as a JS number: every amount is a decimal string
 * (Money on the server, NUMERIC in the database).
 */
// ── The fixed sentences of SAL-00 ───────────────────────────────────────────
export const SALES_MESSAGES = {
    /**
     * G-10 (duplicate invoice detection), the sales side — a WARNING on the
     * write response's `warnings` array, never a refusal: same customer, same
     * grand total, same invoice date as another live invoice.
     */
    duplicateInvoiceWarning: 'Another invoice for this customer has the same total and the same date. Check it is not a duplicate.',
    /** BR-SAL-00-15 / ERR-SAL-00-01. */
    noLines: 'Add at least one line before posting.',
    /** BR-SAL-00-16. */
    zeroTotal: 'A document for 0.00 cannot be posted.',
    /** Wave 4D — a cash invoice with no account picked and no company default. */
    cashAccountRequired: 'Pick the cash account this invoice is paid into, or set a default cash account in Settings.',
    /** Wave 4D — a cash invoice's account must be a cash account (under 1110). */
    cashAccountKind: 'A cash invoice must be paid into a cash account.',
    /** BR-SAL-00-18 / ERR-SAL-00-02. */
    qtyNotPositive: (line) => `Quantity must be more than zero on line ${line}.`,
    /** ERR-SAL-00-03. */
    negativePrice: 'A price cannot be less than zero. Use a discount instead.',
    /** BR-SAL-00-19 / ERR-SAL-00-04. */
    discountRange: 'A discount must be between 0 and 100 percent.',
    /**
     * BR-MD-01-10a's document half — the `invoicing.max_discount_pct` company
     * setting, named in the refusal (ADM-01 §15: a tightened cap fails the
     * check "with a message naming the new limit").
     */
    discountOverMax: (maxPct) => `The most you can give is ${maxPct}%.`,
    /** BR-SAL-00-22 / ERR-SAL-00-06. */
    postedLocked: 'This document is posted. Cancel it, or raise a credit note.',
    /** BR-SAL-00-23 / ERR-SAL-00-07. */
    postedNoDelete: 'A posted document cannot be deleted. Use Cancel.',
    /** SAL-00 §15 "Very large". */
    veryLarge: 'A grand total above 999,999,999.99 cannot be saved.',
    /** Not draft = not postable twice. */
    notADraft: 'Only a draft can be posted.',
    // ── The cancel act (ruling A6) ────────────────────────────────────────────
    /** GEN-70: only the posted state cancels; a draft deletes instead. */
    cancelOnlyPosted: 'Only a posted invoice can be cancelled. A draft is edited or deleted instead.',
    /** GEN-72/GEN-59 — the ask, refused empty by the schema below. */
    cancelReasonRequired: 'Say why this invoice is being cancelled.',
    /**
     * GEN-74's first blocker, and the exact mirror of `BILL_MESSAGES.
     * paymentMatched` on the other side of the ledger — same fact, same
     * remedy, so the same sentence with the nouns swapped. A receipt allocated
     * to this invoice POINTS AT IT, and cancelling underneath would leave the
     * voucher pointing at nothing.
     *
     * The amount is the LIVE allocation, not an example: that is the whole
     * point of putting a number in the sentence.
     */
    receiptMatched: (amount) => `A receipt of ${amount} is matched to this invoice. Unmatch it first.`,
    /**
     * GEN-74's second blocker, in the rule's OWN shape — the spec writes the
     * example as "Cancel INV-2042 first — it was made from this order.", and
     * `BILL_MESSAGES.debitNoteStands` is the same sentence for the debit note.
     * A credit note derives from one posted invoice (BR-SAL-05-02) and copied
     * its figures (BR-SAL-05-04), so it is cancelled first.
     */
    creditNoteStands: (creditCode) => `Cancel ${creditCode} first — it was made from this invoice.`,
    /**
     * BR-SAL-04-11 and its §14 row ERR-SAL-04-04, VERBATIM — including the
     * question mark, because the rule writes it as a question and the screen
     * asks it before the reason box opens.
     *
     * It is a WARNING, not a refusal: ERR-SAL-04-04's "What the user should do"
     * column is "Confirm, or raise a credit note instead", so both roads are
     * open and the sentence exists to make the second one a real choice. The
     * server never returns it — it is the client's pre-flight — which is why it
     * is here beside the refusals rather than thrown by one.
     */
    authorityAccepted: 'This invoice was accepted by the tax authority. Cancelling it will also send a cancellation. Continue?',
};
/**
 * D46 — SAL-04's CONVERTED parameterised refusals, structured: GEN-74's two
 * cancel blockers, each naming a live value (the matched amount; the standing
 * credit note's code). Sentence parity with the `SALES_MESSAGES` twins is
 * pinned by `refusal-census.spec.ts`.
 */
export const SALES_REFUSALS = {
    receiptMatched: (amount) => ({
        message: SALES_MESSAGES.receiptMatched(amount),
        key: 'sales.errReceiptMatched',
        params: { amount },
    }),
    creditNoteStands: (creditCode) => ({
        message: SALES_MESSAGES.creditNoteStands(creditCode),
        key: 'sales.errCreditNoteStands',
        params: { code: creditCode },
    }),
    // ── d46tail: BR-MD-01-10a's discount-cap refusal ─────────────────────────
    /** `maxPct` is PRE-FORMATTED (the company cap as a decimal string) by the
     *  raise site's Money formatter; it rides as a display string. */
    discountOverMax: (maxPct) => ({
        message: SALES_MESSAGES.discountOverMax(maxPct),
        key: 'sales.errDiscountOverMax',
        params: { maxPct },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
/**
 * One grid line (SAL-00 §5d). Item-linked OR free text: itemId names a real
 * catalogue record; absent, the line is free text exactly as before (service
 * rows and one-off charges are real — R14 pragmatism).
 *
 * WHICH DOCUMENT MOVES STOCK (SAL-03 §12's decision, followed exactly):
 * an invoice created FROM a sales order never moves stock — the delivery
 * note owns the movement and the COGS; a DIRECT invoice with product lines
 * moves stock at posting (sale movements at weighted average, DR 5040 /
 * CR 1131 in the same entry). qty is typed in unitId (one of the item's
 * sell units; absent = the stock unit) and stored converted (BR-MD-03-04d).
 */
/**
 * OQ-05-17's sub-table, on the wire: a KIT line's tracked components, one
 * block each. A kit line has no identifiers of its own — it has N parts, and
 * BR-MD-03-17 says each follows its OWN tracking — so a serial-tracked
 * component names one serial per unit deducted and a batch-tracked one names
 * its batch, exactly as if the part had been typed on a line by itself.
 */
export const ComponentTrackingResponseSchema = z
    .object({
    componentItemId: z.uuid(),
    componentName: z.string(),
    serialNos: z.string().array().nullable(),
    batchNo: z.string().nullable(),
})
    .strict();
export const ComponentTrackingSchema = z
    .object({
    componentItemId: z.uuid(),
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
})
    .strict();
export const InvoiceLineInputSchema = z
    .object({
    itemId: z.uuid().optional(),
    /** Required (at posting) for a DIRECT product line — where goods leave from. */
    warehouseId: z.uuid().optional(),
    /** The unit the qty was typed in; absent = the item's stock unit. */
    unitId: z.uuid().optional(),
    /** BR-MD-03 tracking for the direct-invoice path: one serial per unit. */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
    /** R7: a KIT line's per-component capture (BR-MD-03-17). */
    components: ComponentTrackingSchema.array().max(200).optional(),
    description: z.string().trim().min(1).max(200),
    qty: QtyStringSchema,
    /**
     * INV-L1 — the free quantity given with the line, in the line's unit.
     * Omitted = 0. Leaves stock with qty at posting at zero revenue; the
     * company's `invoicing.line_bonus` switch decides whether the grid draws
     * the column at all.
     */
    bonusQty: BonusQtyStringSchema.optional(),
    unitPrice: MoneyStringSchema,
    /** BR-SAL-00-19. */
    discountPct: PctStringSchema.optional(),
})
    .strict();
/**
 * F-2/F-4 — the two-way boxes: the caller sends WHICH box was typed and the
 * server derives the other, so neither box is the master.
 */
export const TwoWayAmountSchema = z.union([
    z.object({ pct: PctStringSchema }).strict(),
    z.object({ amount: MoneyStringSchema }).strict(),
]);
/**
 * INV-F1…F6 (bug triage Oct 2026) — the header fields every SALES document
 * carries, written ONCE and spread into the invoice, the quotation and the
 * sales order so QUO-01 / SO-02's "same as the sales invoice in every detail"
 * is a property of one shape rather than three copies agreeing.
 *
 *   warehouseId        INV-F1 — the document's default warehouse: a line with
 *                      no warehouse of its own ships from here (INV-L3) and
 *                      may still override it per line.
 *   projectId          INV-F2 — the project this sale belongs to.
 *   partnerBranchId    INV-F3 — the customer's branch, a RECORD of the
 *                      partner's (`partnersContract.listBranches`); must be
 *                      one of the document's partner's own (400 otherwise).
 *                      The server writes the branch's NAME into
 *                      `partnerBranch` beside it, so every reader of the
 *                      text keeps working.
 *   partnerBranch      the branch as TEXT — the interim free-text column,
 *                      kept: documents written before branches were records
 *                      carry only this, and a caller may still send it alone.
 *   manualRef, bookNo  INV-F4 — the hand-written reference and the paper
 *                      booklet number a business keys beside its own.
 *   salespersonUserId  INV-F6 — the user who made the sale; the screen defaults
 *                      it to whoever is typing a NEW document.
 *
 * Three spellings of one set: the CREATE shape (optional), the PATCH shape
 * (null clears) and the RESPONSE shape (nullable, with the names the screen
 * prints resolved server-side so a reader without the warehouse, project or
 * user list permission still sees them).
 */
export const SalesHeaderInputFields = {
    warehouseId: z.uuid().optional(),
    projectId: z.uuid().optional(),
    partnerBranchId: z.uuid().optional(),
    partnerBranch: z.string().trim().max(120).optional(),
    manualRef: z.string().trim().max(60).optional(),
    bookNo: z.string().trim().max(40).optional(),
    salespersonUserId: z.uuid().optional(),
};
export const SalesHeaderPatchFields = {
    warehouseId: z.uuid().nullable().optional(),
    projectId: z.uuid().nullable().optional(),
    partnerBranchId: z.uuid().nullable().optional(),
    partnerBranch: z.string().trim().max(120).nullable().optional(),
    manualRef: z.string().trim().max(60).nullable().optional(),
    bookNo: z.string().trim().max(40).nullable().optional(),
    salespersonUserId: z.uuid().nullable().optional(),
};
/**
 * WAVE 4D — THE SALES COMMISSION, on the four sales money documents
 * (quotation, order, invoice, credit note).
 *
 *   commissionPct     the rep's commission, % of the document's NET
 *                     (subtotal less the document discount, before tax).
 *                     On CREATE, absent = the default: the quotation's
 *                     sales-rep employee's `defaultCommissionPct`, else the
 *                     employee linked to the salesperson user; carried
 *                     quotation → order → invoice and invoice → credit note
 *                     (so a return carries the invoice's rate). On PATCH,
 *                     null clears it. Draft only, with the rest of the row.
 *   commissionAmount  net × pct ÷ 100, a Money string in the document's
 *                     currency; derived on read, never stored. On a credit
 *                     note it is the share that REVERSES. Null with no pct.
 *
 * A commission REPORT is out of scope for this wave (see the module notes).
 */
export const CommissionInputFields = {
    commissionPct: PctStringSchema.optional(),
};
export const CommissionPatchFields = {
    commissionPct: PctStringSchema.nullable().optional(),
};
export const CommissionResponseFields = {
    commissionPct: z.string().nullable(),
    commissionAmount: z.string().nullable(),
};
export const SalesHeaderResponseFields = {
    warehouseId: z.uuid().nullable(),
    warehouseName: z.string().nullable(),
    projectId: z.uuid().nullable(),
    projectName: z.string().nullable(),
    partnerBranchId: z.uuid().nullable(),
    /** The branch record's CURRENT name (null when none is picked or it is gone). */
    partnerBranchName: z.string().nullable(),
    partnerBranch: z.string().nullable(),
    manualRef: z.string().nullable(),
    bookNo: z.string().nullable(),
    salespersonUserId: z.uuid().nullable(),
    salespersonName: z.string().nullable(),
};
export const InvoiceLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    itemId: z.uuid().nullable(),
    itemCode: z.string().nullable(),
    warehouseId: z.uuid().nullable(),
    unitId: z.uuid().nullable(),
    /**
     * N-4 — the unit `qty` IS IN, resolved for display: `unitId`'s symbol and
     * decimals, or the item's stock unit when `unitId` is null. Both null on a
     * free-text line, which has no item and therefore no unit of measure.
     *
     * Resolved on the server rather than looked up by the screen because the
     * SPA's instant-print path builds the printed document from this response
     * alone — a screen that had to fetch units to print would print the wrong
     * quantity whenever the fetch had not landed.
     */
    unitSymbol: z.string().nullable(),
    unitPrecision: UnitPrecisionSchema.nullable(),
    /** BR-MD-03-04d: qty converted to the item's stock unit; null = free text. */
    baseQty: z.string().nullable(),
    /**
     * BR-MD-12-10 / GEN-120's "must show both": the STOCK unit that `baseQty`
     * is in — "2 BOX (1,000 PCS)" needs both halves named. Non-null ONLY when
     * the line was typed in a unit other than the item's stock unit; null on a
     * stock-unit line (nothing to show twice) and on a free-text line (no
     * unit at all). Resolved on the server for the same reason `unitSymbol`
     * is: the screen must not need a second fetch to say what "1,000" means.
     */
    stockUnitSymbol: z.string().nullable(),
    stockUnitPrecision: UnitPrecisionSchema.nullable(),
    serialNos: z.string().array().nullable(),
    batchNo: z.string().nullable(),
    /** R7's sub-table as stored; empty for anything that is not a kit line. */
    components: ComponentTrackingResponseSchema.array(),
    description: z.string(),
    qty: z.string(),
    /** INV-L1 — the free quantity beside qty; "0.000" on an ordinary line. */
    bonusQty: z.string(),
    unitPrice: z.string(),
    discountPct: z.string(),
    /** F-1, rounded to 2 (BR-SAL-00-08). */
    lineTotal: z.string(),
})
    .strict();
export const InvoiceStatusSchema = z.enum(['draft', 'posted', 'cancelled']);
export const InvoiceSchema = z
    .object({
    id: z.uuid(),
    /** INV-#### — the sales invoice's own series (BR-SAL-00-28). */
    code: z.string(),
    invoiceNo: z.number().int().positive(),
    status: InvoiceStatusSchema,
    invoiceDate: IsoDateSchema,
    dueDate: IsoDateSchema,
    partnerId: z.uuid(),
    partnerCode: z.string(),
    partnerName: z.string(),
    /**
     * G-11 — the per-customer running reference "<partner code>-<n>",
     * allotted at POSTING (n = this customer's posted invoices so far + 1,
     * per tenant, inside the posting transaction with the partner row
     * locked). Null while a draft. A SECONDARY reference: the legal `code`
     * is unchanged, and the list's `q` matches either.
     */
    partnerRef: z.string().nullable(),
    ...SalesHeaderResponseFields,
    /**
     * Wave 3B — the chain the invoice came out of, so the list's stepper and
     * link chips can show it (`invoices.sales_order_id`, written by SAL-02's
     * Create invoice). Both null on a direct invoice; `deliveryNoteCodes` is
     * the POSTED delivery notes of that order (the goods that left), empty
     * when none has posted or there is no order.
     */
    salesOrderId: z.uuid().nullable(),
    salesOrderCode: z.string().nullable(),
    deliveryNoteCodes: z.string().array(),
    /**
     * Wave 4D — THE CASH INVOICE. `isCash` invoices are paid at the counter:
     * posting one also creates and posts a receipt for the full grand total
     * into `cashAccountId`, allocated to this invoice, in the same
     * transaction; cancelling one reverses that receipt first.
     * `cashReceiptId`/`cashReceiptCode` name that receipt (null until posted).
     */
    isCash: z.boolean(),
    cashAccountId: z.uuid().nullable(),
    cashAccountCode: z.string().nullable(),
    cashReceiptId: z.uuid().nullable(),
    cashReceiptCode: z.string().nullable(),
    currency: z.string(),
    /** EC-19-10 / BR-SAL-04-09: the rate frozen at posting, and its "as of" date. */
    ...frozenRateFields,
    /** Wave 4D — the typed-rate flag and the market hint (fx.ts). */
    ...rateOverrideFields,
    /** Wave 4D — the sales commission (sales-invoices.ts). */
    ...CommissionResponseFields,
    lines: InvoiceLineSchema.array(),
    /** The stored PART 18 block: F-5, F-2, F-3-basis, F-4, F-7. */
    subtotal: z.string(),
    discountPct: z.string(),
    discountAmount: z.string(),
    taxPct: z.string(),
    taxAmount: z.string(),
    grandTotal: z.string(),
    /** F-8 — Σ allocations from POSTED receipts (R9: drafts settle nothing). */
    amountPaid: z.string(),
    /** F-9 — grandTotal − amountPaid. */
    balanceDue: z.string(),
    /** F-9's tolerance test: balance ≤ 0.0001, never an exact zero. */
    fullyPaid: z.boolean(),
    /**
     * BR-MD-09-09: "An invoice past its due date and not fully paid must
     * show the Past Due chip in amber, turning red after 30 days past due."
     * Derived by the SERVER from the company clock (N-16) — posted, unpaid,
     * due date before today; the client colours what it is told (the MD-01
     * partner-chip precedent). Null when the chip does not draw.
     */
    pastDue: z.enum(['amber', 'red']).nullable(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    postedAt: z.string().nullable(),
    /**
     * Ruling A6's three stamps. Non-null together or null together — the
     * `invoices_cancel_stamped` CHECK is the floor — except
     * `cancellationJournalEntryId`, which is null on the (impossible today)
     * case of a posted invoice carrying no entry.
     *
     * `cancelReason` is on the WIRE, not only in the audit log, because the
     * screen shows it back: a cancelled document that will not say why is the
     * defect `receipts.cancelledReasonNotice` was written to end.
     */
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    cancellationJournalEntryId: z.uuid().nullable(),
    createdAt: z.string(),
    /**
     * G-10 — non-blocking warnings from a WRITE (create/update/post):
     * `SALES_MESSAGES.duplicateInvoiceWarning`. Absent on reads and lists.
     */
    warnings: z.string().array().optional(),
})
    .strict();
export const CreateInvoiceRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverrideInputFields,
    /** Per-document currency (fx.ts documentCurrencyInputFields). */
    ...documentCurrencyInputFields,
    /** Wave 4D — the sales commission % (sales-invoices.ts). */
    ...CommissionInputFields,
    partnerId: z.uuid(),
    invoiceDate: IsoDateSchema,
    /** Defaults from the partner's payment terms (BR-MD-01-11); no terms = the invoice date. */
    dueDate: IsoDateSchema.optional(),
    ...SalesHeaderInputFields,
    lines: InvoiceLineInputSchema.array().min(1),
    /** F-2 — omitted = no document discount. */
    discount: TwoWayAmountSchema.optional(),
    /** F-4 — omitted = the seeded default rate (15% until org settings exist). */
    tax: TwoWayAmountSchema.optional(),
    notes: z.string().max(2000).optional(),
    /** Wave 4D — a cash invoice; the account defaults to `invoicing.default_cash_account`. */
    isCash: z.boolean().optional(),
    cashAccountId: z.uuid().optional(),
})
    .strict();
export const UpdateInvoiceRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverridePatchFields,
    /** Per-document currency, draft only (fx.ts documentCurrencyPatchFields). */
    ...documentCurrencyPatchFields,
    /** Wave 4D — the sales commission % (sales-invoices.ts). */
    ...CommissionPatchFields,
    invoiceDate: IsoDateSchema.optional(),
    dueDate: IsoDateSchema.optional(),
    ...SalesHeaderPatchFields,
    /** The grid saves as a set: present = replace all lines (draft only). */
    lines: InvoiceLineInputSchema.array().min(1).optional(),
    discount: TwoWayAmountSchema.optional(),
    tax: TwoWayAmountSchema.optional(),
    /** The ONE field editable after posting (BR-SAL-00-22). */
    notes: z.string().max(2000).nullable().optional(),
    /** Wave 4D — draft only; false clears the account, null re-takes the default. */
    isCash: z.boolean().optional(),
    cashAccountId: z.uuid().nullable().optional(),
})
    .strict();
/**
 * GEN-72's ask, in the family's established schema — copied field for field
 * from `CancelBillRequestSchema` and the delivery note's, because a reason is
 * a reason and five documents disagreeing about its bounds would be five
 * different products.
 */
export const CancelInvoiceRequestSchema = z
    .object({
    reason: z.string().trim().min(1, SALES_MESSAGES.cancelReasonRequired).max(500),
})
    .strict();
const invoiceParams = z.object({ invoiceId: z.uuid() }).strict();
export const invoicesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/sales/invoices',
        query: pagedQuery({
            partnerId: z.uuid().optional(),
            status: InvoiceStatusSchema.optional(),
            /** POSTED invoices with a balance above the F-9 tolerance. */
            openOnly: z.enum(['true', 'false']).optional(),
            /**
             * The list's search box: the printed number, the customer's name and
             * — G-11 — the per-customer reference (`partnerRef`).
             */
            q: z.string().trim().max(120).optional(),
            /** Wave 3B — bounds on the invoice date, both inclusive. */
            ...DocumentDateRangeQuery,
        }),
        responses: {
            200: pagedResponse('invoices', InvoiceSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Sales invoices (sales.view), PAGED (NFR-42). Drafts appear HERE and nowhere else (BR-SAL-00-27).',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail: the invoices on either side of
     * this one in number order (null at an end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/sales/invoices/:invoiceId/neighbours',
        pathParams: invoiceParams,
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next invoice by number (sales.view).',
    },
    /** S4 — the "Go to no." box: one invoice by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/sales/invoices/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One invoice by its printed number (sales.view).',
    },
    get: {
        method: 'GET',
        path: '/api/sales/invoices/:invoiceId',
        pathParams: invoiceParams,
        responses: {
            200: InvoiceSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One invoice with its lines (sales.view).',
    },
    create: {
        method: 'POST',
        path: '/api/sales/invoices',
        body: CreateInvoiceRequestSchema,
        responses: {
            201: InvoiceSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft (sales.create). Totals computed per PART 18 F-1..F-7 through Money; partner must be an active customer (BR-MD-01-07).',
    },
    update: {
        method: 'PATCH',
        path: '/api/sales/invoices/:invoiceId',
        pathParams: invoiceParams,
        body: UpdateInvoiceRequestSchema,
        responses: {
            200: InvoiceSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (sales.edit); a posted invoice answers 409 with the BR-SAL-00-22 sentence — except a notes-only change, which is the one legal posted edit.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/sales/invoices/:invoiceId',
        pathParams: invoiceParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (sales.delete) — "a draft may be deleted freely" (R8); posted answers 409 (BR-SAL-00-23).',
    },
    post: {
        method: 'POST',
        path: '/api/sales/invoices/:invoiceId/post',
        pathParams: invoiceParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: InvoiceSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post invoice (sales.approve) — Draft → Posted: writes the SAL-00 §12 entry (DR receivables / CR revenue + VAT) through the ledger in ONE transaction and locks the document.',
    },
    cancel: {
        method: 'POST',
        path: '/api/sales/invoices/:invoiceId/cancel',
        pathParams: invoiceParams,
        body: CancelInvoiceRequestSchema,
        responses: {
            200: InvoiceSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Cancel a POSTED invoice (sales.approve — ruling A6, R8's reversal discipline): reverses the §12 entry on the invoice's own date at the document's frozen rate (GEN-72, D19), returns a DIRECT invoice's goods at the cost they left at, re-derives the source order's invoiced percentage, and raises the authority notification when the tax authority had accepted it (BR-SAL-04-11/GEN-134). Refused while a posted receipt is allocated or a posted credit note stands (GEN-74, the blocker named), and into a closed period (GEN-73). Reason required, stored and audited (invoice.cancel).",
    },
    /** Wave 3B — the list's tiles over the WHOLE register (see `document-stats.ts`). */
    stats: {
        method: 'GET',
        path: '/api/sales/invoices/stats',
        responses: {
            200: InvoiceStatsSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'SAL-04 list tiles (sales.view): outstanding, overdue count and value, drafts, collected this month — posted-only, base currency, in SQL.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=sales-invoices.js.map